import { runWorkerPool } from './workerPool';

/** Specs a bulk update can change. A missing key keeps the current value. */
export interface BulkSpecs {
    material?: number;
    finishes?: number[];
    coatings?: number[];
}

export type BulkResultStatus = 'updated' | 'unchanged' | 'skipped' | 'failed';

export interface BulkResult {
    id: number;
    status: BulkResultStatus;
    reason?: string;
    line_item?: {
        safe_auto_quote?: boolean;
        needs_manual_quote?: boolean;
        is_pricing_in_progress?: boolean;
    } & Record<string, unknown>;
}

export interface BulkPart {
    key: string;
    lineItemIds: number[];
}

export interface BulkSummary {
    updated: number;
    unchanged: number;
    skipped: number;
    failed: number;
    /** Of the updated lines: instant price, manual quote, or still waiting for the analysis. */
    priced: number;
    manual: number;
    waiting: number;
}

/** The server accepts at most 50 lines per request. */
export const MAX_LINES_PER_REQUEST = 50;
/** Each changed line may make external pricing calls, so requests stay small. */
export const DEFAULT_PARTS_PER_REQUEST = 3;
export const DEFAULT_CONCURRENCY = 2;

export const hasSpecs = (specs: BulkSpecs): boolean =>
    specs.material != null || specs.finishes != null || specs.coatings != null;

/** The request body: only the specs chosen (omitted = keep). */
export const buildSpecsPayload = (lineItemIds: number[], specs: BulkSpecs, dryRun = false) => ({
    line_item_ids: lineItemIds,
    ...(specs.material != null ? { material: specs.material } : {}),
    ...(specs.finishes != null ? { finishes: specs.finishes } : {}),
    ...(specs.coatings != null ? { coatings: specs.coatings } : {}),
    ...(dryRun ? { dry_run: true } : {}),
});

/**
 * Groups parts into requests. All variants of a part travel together (the server also
 * changes them together, so splitting them would make two requests write the same lines),
 * and a request never exceeds the line limit.
 */
export const chunkParts = (
    parts: BulkPart[],
    partsPerRequest = DEFAULT_PARTS_PER_REQUEST,
    maxLines = MAX_LINES_PER_REQUEST
): number[][] => {
    const chunks: number[][] = [];
    let current: number[] = [];
    let currentParts = 0;
    parts.forEach(part => {
        const full =
            currentParts >= partsPerRequest ||
            (current.length > 0 && current.length + part.lineItemIds.length > maxLines);
        if (full) {
            chunks.push(current);
            current = [];
            currentParts = 0;
        }
        current.push(...part.lineItemIds);
        currentParts += 1;
    });
    if (current.length > 0) chunks.push(current);
    return chunks;
};

export const summarizeResults = (results: BulkResult[]): BulkSummary => {
    const summary: BulkSummary = { updated: 0, unchanged: 0, skipped: 0, failed: 0, priced: 0, manual: 0, waiting: 0 };
    results.forEach(result => {
        summary[result.status] += 1;
        if (result.status !== 'updated') return;
        const item = result.line_item;
        if (item?.safe_auto_quote && !item?.needs_manual_quote) summary.priced += 1;
        else if (item?.is_pricing_in_progress) summary.waiting += 1;
        else summary.manual += 1;
    });
    return summary;
};

/** "28 priced instantly · 9 manual quote · 3 waiting for analysis", only the non-zero parts. */
export const describeSummary = (summary: BulkSummary): string => {
    const parts: string[] = [];
    if (summary.priced) parts.push(`${summary.priced} priced instantly`);
    if (summary.manual) parts.push(`${summary.manual} manual quote`);
    if (summary.waiting) parts.push(`${summary.waiting} waiting for analysis`);
    if (summary.unchanged) parts.push(`${summary.unchanged} already matched`);
    if (summary.skipped) parts.push(`${summary.skipped} skipped`);
    if (summary.failed) parts.push(`${summary.failed} failed`);
    return parts.join(' · ');
};

/** "9 will change · 3 already match · 2 skipped" for the dry run. */
export const describePreview = (summary: BulkSummary): string => {
    const parts: string[] = [];
    if (summary.updated) parts.push(`${summary.updated} will change`);
    if (summary.unchanged) parts.push(`${summary.unchanged} already match`);
    if (summary.skipped) parts.push(`${summary.skipped} skipped`);
    return parts.join(' · ');
};

/** The text messages inside a validation error body ({ field: ['message'] }). */
const errorMessages = (value: unknown): string[] => {
    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) return value.flatMap(errorMessages);
    if (value && typeof value === 'object') return Object.values(value).flatMap(errorMessages);
    return [];
};

export const failureReason = (error: unknown): string => {
    const response = (error as { response?: { data?: unknown; status?: number } })?.response;
    const detail = response?.data;
    // A 500 page or a gateway timeout comes back as an HTML document: never show it.
    if (typeof detail === 'string' && /^\s*</.test(detail)) {
        return response?.status ? `Server error (${response.status})` : 'Server error';
    }
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        const messages = errorMessages(detail);
        return messages.length ? messages.join(' ') : JSON.stringify(detail);
    }
    return (error as Error)?.message || 'Request failed';
};

/** What the preview says when some part could not be checked: the reason, if there is one. */
export const describeFailedCheck = <T extends { status: string; reason?: string }>(results: T[]): string => {
    const reason = results.find(result => result.status === 'failed' && result.reason)?.reason;
    return reason ? `Couldn't check every part: ${reason}` : "Couldn't check every part.";
};

export interface RunChunkedOptions<T> {
    parts: BulkPart[];
    /** Sends one request; resolves with the server results for its lines. */
    send: (lineItemIds: number[]) => Promise<T[]>;
    /** Results standing for the lines of a request that failed as a whole (network, 5xx). */
    failed: (lineItemIds: number[], reason: string) => T[];
    partsPerRequest?: number;
    /** Most lines a request may carry (default: the server cap). */
    maxLines?: number;
    concurrency?: number;
    /** Called after each request with all results so far. */
    onProgress?: (results: T[], total: number) => void;
}

/**
 * Sends the parts request by request with a small concurrency. A request that fails marks
 * its lines as failed instead of aborting the rest, so "Retry failed" can run them again.
 */
export const runChunked = async <T>({
    parts,
    send,
    failed,
    partsPerRequest = DEFAULT_PARTS_PER_REQUEST,
    maxLines,
    concurrency = DEFAULT_CONCURRENCY,
    onProgress,
}: RunChunkedOptions<T>): Promise<T[]> => {
    const queue = chunkParts(parts, partsPerRequest, maxLines);
    const total = parts.reduce((sum, part) => sum + part.lineItemIds.length, 0);
    const results: T[] = [];

    await runWorkerPool(
        () => queue.shift(),
        async ids => {
            try {
                results.push(...(await send(ids)));
            } catch (error) {
                results.push(...failed(ids, failureReason(error)));
            }
            onProgress?.([...results], total);
        },
        concurrency
    );

    return results;
};

export type RunBulkSpecsOptions = Omit<RunChunkedOptions<BulkResult>, 'failed'>;

/** Applies the specs request by request (see `runChunked`). */
export const runBulkSpecs = (options: RunBulkSpecsOptions): Promise<BulkResult[]> =>
    runChunked<BulkResult>({
        ...options,
        failed: (ids, reason) => ids.map(id => ({ id, status: 'failed' as const, reason })),
    });

/** Parts that still have a failed line, to retry only those. */
export const failedParts = (parts: BulkPart[], results: BulkResult[]): BulkPart[] => {
    const failed = new Set(results.filter(r => r.status === 'failed').map(r => r.id));
    return parts.filter(part => part.lineItemIds.some(id => failed.has(id)));
};
